import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Save, ArrowLeft, Eye, Loader2 } from "lucide-react";
import {
    adminCreatePost, adminGetPost, adminUpdatePost, formatApiError,
} from "../../lib/adminApi";
import { CATEGORIES } from "../../lib/updates";
import { sanitizeHtml } from "../../lib/sanitize";

const TEMPLATE = `# Overskrift

Skriv din intro her.

## Underafsnit

- Punkt 1
- Punkt 2

> Et citat eller en pointe der skal stå ud.
`;

const empty = {
    title: "",
    slug: "",
    category: "produkt",
    excerpt: "",
    body: TEMPLATE,
    cover: "",
    author: "Cleany Team",
    reading_time: 3,
    date: new Date().toISOString().slice(0, 10),
    published: true,
};

function slugify(value) {
    return value.toLowerCase().trim()
        .replace(/[æå]/g, "a").replace(/ø/g, "o")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 80);
}

export default function AdminPostEditor() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const isNew = !slug;

    const [form, setForm] = useState(empty);
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [readOnly, setReadOnly] = useState(false);
    const [slugTouched, setSlugTouched] = useState(false);

    useEffect(() => {
        document.title = isNew ? "Nyt indlæg — Admin" : "Rediger indlæg — Admin";
    }, [isNew]);

    useEffect(() => {
        if (isNew) return;
        setLoading(true);
        adminGetPost(slug)
            .then((p) => {
                setForm({
                    title: p.title || "",
                    slug: p.slug || "",
                    category: p.category || "produkt",
                    excerpt: p.excerpt || "",
                    body: p.body || "",
                    cover: p.cover || "",
                    author: p.author || "Cleany Team",
                    reading_time: p.reading_time || 3,
                    date: (p.date || "").slice(0, 10),
                    published: p.published !== false,
                });
                setReadOnly(p.source === "file");
                setSlugTouched(true);
            })
            .catch((err) => {
                toast.error(formatApiError(err));
                navigate("/admin/posts");
            })
            .finally(() => setLoading(false));
    }, [slug, isNew, navigate]);

    // Auto-suggest slug from title until user explicitly edits it
    const onTitleChange = (v) => {
        setForm((f) => ({
            ...f,
            title: v,
            slug: slugTouched ? f.slug : slugify(v),
        }));
    };

    const submit = async (e) => {
        e.preventDefault();
        if (readOnly) {
            toast.error("Markdown-filer kan ikke redigeres herfra — rediger filen i kodebasen.");
            return;
        }
        setSaving(true);
        const payload = {
            ...form,
            slug: form.slug || slugify(form.title),
            reading_time: Number(form.reading_time) || 3,
        };
        try {
            if (isNew) {
                const res = await adminCreatePost(payload);
                toast.success("Indlæg oprettet");
                navigate(`/admin/posts/${res.slug}`, { replace: true });
            } else {
                const res = await adminUpdatePost(slug, payload);
                toast.success("Ændringer gemt");
                if (res.slug !== slug) {
                    navigate(`/admin/posts/${res.slug}`, { replace: true });
                }
            }
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="p-10 text-center text-slate-500">Henter…</div>;
    }

    return (
        <div data-testid="admin-post-editor">
            <div className="flex items-center justify-between gap-4">
                <div>
                    <Link
                        to="/admin/posts"
                        data-testid="editor-back-link"
                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-sky-700 hover:text-sky-900"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Tilbage til indlæg
                    </Link>
                    <h1 className="mt-2 font-display text-3xl font-bold tracking-tight text-slate-900">
                        {isNew ? "Nyt indlæg" : "Rediger indlæg"}
                    </h1>
                </div>
                <div className="flex items-center gap-2">
                    {!isNew && form.slug && (
                        <a
                            href={`/updates/${form.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold px-4 h-10 text-sm transition-colors"
                        >
                            <Eye className="h-4 w-4" />
                            Vis live
                        </a>
                    )}
                </div>
            </div>

            {readOnly && (
                <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 text-amber-900 px-4 py-3 text-sm" data-testid="editor-file-warning">
                    Dette indlæg er en markdown-fil i kodebasen og kan ikke redigeres herfra.
                    Rediger filen direkte i <code className="text-xs bg-white border border-amber-200 px-1 rounded">/app/content/posts/</code> for at ændre indholdet.
                </div>
            )}

            <form onSubmit={submit} className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main column */}
                <div className="lg:col-span-2 space-y-5">
                    <Field label="Titel" required>
                        <input
                            type="text"
                            value={form.title}
                            onChange={(e) => onTitleChange(e.target.value)}
                            required maxLength={200}
                            disabled={readOnly}
                            data-testid="editor-title"
                            className={inputCls}
                        />
                    </Field>

                    <Field label="Slug (URL)" hint="Genereres automatisk. Påvirker URL'en /updates/{slug}.">
                        <input
                            type="text"
                            value={form.slug}
                            onChange={(e) => { setForm({ ...form, slug: e.target.value }); setSlugTouched(true); }}
                            placeholder="auto-genereres"
                            pattern="[a-z0-9-]*"
                            disabled={readOnly || !isNew}
                            data-testid="editor-slug"
                            className={inputCls}
                        />
                    </Field>

                    <Field label="Excerpt" required hint="Kort opsummering vist i listen og som meta-description.">
                        <textarea
                            value={form.excerpt}
                            onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                            required minLength={10} maxLength={400} rows={3}
                            disabled={readOnly}
                            data-testid="editor-excerpt"
                            className={inputCls}
                        />
                    </Field>

                    <Field label="Brødtekst (Markdown)" required>
                        <BodyEditor
                            value={form.body}
                            onChange={(v) => setForm({ ...form, body: v })}
                            readOnly={readOnly}
                        />
                    </Field>
                </div>

                {/* Side column */}
                <aside className="space-y-5">
                    <div className="rounded-2xl bg-white border border-slate-200 p-5 space-y-5">
                        <Field label="Status">
                            <label className="flex items-center gap-2 text-sm text-slate-700">
                                <input
                                    type="checkbox"
                                    checked={form.published}
                                    onChange={(e) => setForm({ ...form, published: e.target.checked })}
                                    disabled={readOnly}
                                    data-testid="editor-published"
                                    className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                                />
                                Publicer (synlig på /updates)
                            </label>
                        </Field>

                        <Field label="Kategori" required>
                            <select
                                value={form.category}
                                onChange={(e) => setForm({ ...form, category: e.target.value })}
                                disabled={readOnly}
                                data-testid="editor-category"
                                className={inputCls}
                            >
                                {CATEGORIES.map((c) => (
                                    <option key={c.slug} value={c.slug}>{c.label}</option>
                                ))}
                            </select>
                        </Field>

                        <Field label="Dato">
                            <input
                                type="date"
                                value={form.date}
                                onChange={(e) => setForm({ ...form, date: e.target.value })}
                                disabled={readOnly}
                                data-testid="editor-date"
                                className={inputCls}
                            />
                        </Field>

                        <Field label="Cover billede URL" hint="Fx /updates-images/min-cover.svg">
                            <input
                                type="text"
                                value={form.cover}
                                onChange={(e) => setForm({ ...form, cover: e.target.value })}
                                placeholder="/updates-images/..."
                                disabled={readOnly}
                                data-testid="editor-cover"
                                className={inputCls}
                            />
                        </Field>

                        <Field label="Forfatter">
                            <input
                                type="text"
                                value={form.author}
                                onChange={(e) => setForm({ ...form, author: e.target.value })}
                                disabled={readOnly}
                                data-testid="editor-author"
                                className={inputCls}
                            />
                        </Field>

                        <Field label="Læsetid (minutter)">
                            <input
                                type="number" min="1" max="60"
                                value={form.reading_time}
                                onChange={(e) => setForm({ ...form, reading_time: e.target.value })}
                                disabled={readOnly}
                                data-testid="editor-reading-time"
                                className={inputCls}
                            />
                        </Field>
                    </div>

                    <button
                        type="submit"
                        disabled={saving || readOnly}
                        data-testid="editor-save"
                        className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white font-semibold h-11 transition-colors"
                    >
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        {isNew ? "Opret indlæg" : "Gem ændringer"}
                    </button>
                </aside>
            </form>
        </div>
    );
}

function Field({ label, hint, required, children }) {
    return (
        <label className="block">
            <span className="block text-xs font-semibold text-slate-700 mb-1.5">
                {label}{required && <span className="text-rose-500 ml-0.5">*</span>}
            </span>
            {children}
            {hint && <span className="block mt-1 text-[11px] text-slate-500">{hint}</span>}
        </label>
    );
}

const inputCls = "w-full h-10 px-3 rounded-lg border border-slate-300 focus:border-sky-500 focus:ring-2 focus:ring-sky-100 focus:outline-none text-sm disabled:bg-slate-50 disabled:text-slate-500";

function BodyEditor({ value, onChange, readOnly }) {
    const [mode, setMode] = useState("write");
    const previewHtml = useMemo(() => renderMarkdownPreview(value), [value]);

    return (
        <div className="rounded-lg border border-slate-300 overflow-hidden">
            <div className="flex items-center gap-1 px-2 py-1.5 bg-slate-50 border-b border-slate-200">
                <ModeBtn active={mode === "write"} onClick={() => setMode("write")} testid="editor-mode-write">Skriv</ModeBtn>
                <ModeBtn active={mode === "preview"} onClick={() => setMode("preview")} testid="editor-mode-preview">Forhåndsvis</ModeBtn>
                <span className="ml-auto text-[11px] text-slate-500 pr-2">Markdown</span>
            </div>
            {mode === "write" ? (
                <textarea
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    rows={16}
                    disabled={readOnly}
                    data-testid="editor-body"
                    className="w-full p-3 text-sm font-mono leading-relaxed resize-y focus:outline-none disabled:bg-slate-50"
                    placeholder="Skriv i Markdown…"
                />
            ) : (
                <div
                    className="prose-cleany p-5 min-h-[300px]"
                    data-testid="editor-preview"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(previewHtml) || "<p class='text-slate-400'>Skriv noget for at se forhåndsvisning…</p>" }}
                />
            )}
        </div>
    );
}

function ModeBtn({ active, onClick, children, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            className={
                "rounded-md px-2.5 h-7 text-[11px] font-semibold transition-colors " +
                (active
                    ? "bg-white text-slate-900 shadow-sm border border-slate-200"
                    : "text-slate-600 hover:bg-slate-100")
            }
        >
            {children}
        </button>
    );
}

/* Very small markdown subset preview — good enough for editing UX.
   The real rendering happens server-side via Python's `markdown` lib. */
function renderMarkdownPreview(src) {
    if (!src) return "";
    const escape = (s) => s.replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" })[c]);
    const lines = src.split("\n");
    const out = [];
    let inList = null; // "ul" | "ol" | null
    const closeList = () => { if (inList) { out.push(`</${inList}>`); inList = null; } };

    for (let line of lines) {
        if (/^###\s+/.test(line)) { closeList(); out.push(`<h3>${inlineMd(escape(line.replace(/^###\s+/, "")))}</h3>`); continue; }
        if (/^##\s+/.test(line))  { closeList(); out.push(`<h2>${inlineMd(escape(line.replace(/^##\s+/, "")))}</h2>`);  continue; }
        if (/^#\s+/.test(line))   { closeList(); out.push(`<h2>${inlineMd(escape(line.replace(/^#\s+/, "")))}</h2>`);   continue; }
        if (/^>\s+/.test(line))   { closeList(); out.push(`<blockquote><p>${inlineMd(escape(line.replace(/^>\s+/, "")))}</p></blockquote>`); continue; }
        if (/^-\s+/.test(line))   { if (inList !== "ul") { closeList(); inList = "ul"; out.push("<ul>"); } out.push(`<li>${inlineMd(escape(line.replace(/^-\s+/, "")))}</li>`); continue; }
        if (/^\d+\.\s+/.test(line)) { if (inList !== "ol") { closeList(); inList = "ol"; out.push("<ol>"); } out.push(`<li>${inlineMd(escape(line.replace(/^\d+\.\s+/, "")))}</li>`); continue; }
        if (line.trim() === "")   { closeList(); continue; }
        closeList();
        out.push(`<p>${inlineMd(escape(line))}</p>`);
    }
    closeList();
    return out.join("\n");
}

function inlineMd(s) {
    return s
        .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
        .replace(/\*(.+?)\*/g, "<em>$1</em>")
        .replace(/`([^`]+)`/g, "<code>$1</code>")
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');
}
